import React, {useEffect, useRef, useState} from 'react';
import GoogleButton from './GoogleButton';
import GlobalLoaderBackdrop from './GlobalLoaderBackdrop';
import {
  deferTask,
  openUrl,
  TrackEventUBA,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {userService} from '@jobseeker/common-core/services';
import {Divider, Typography, type SxProps, type Theme} from '@mui/material';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME || '';
const isAppHirist = PLATFORM === 'hirist';

const unwrapRedirect = (input?: string): string | undefined => {
  if (!input) return undefined;
  try {
    const url = new URL(input, window.location.origin);
    const nested = url.searchParams.get('redirectto');
    return nested ? decodeURIComponent(nested) : url.pathname + url.search;
  } catch {
    return undefined;
  }
};

const parseTalentBridge = (
  path: string
): {city: string; ref: string | null} | null => {
  try {
    const url = new URL(path, window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    if (
      (parts[0] !== 'talent-bridge-hiring-drive' &&
        parts[0] !== 'talent-bridge-detail') ||
      !parts[1]
    )
      return null;
    return {city: parts[1], ref: url.searchParams.get('ref')};
  } catch {
    return null;
  }
};

const ensureGoogleScript = async (): Promise<void> => {
  // @ts-ignore
  if ((window as any)?.google?.accounts?.id) return;
  await new Promise<void>((resolve, reject) => {
    const scriptId = 'google-identity-services';
    if (document.getElementById(scriptId)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = scriptId;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google script'));
    document.body.appendChild(script);
  });
};

type GoogleLoginProps = {
  buttonSx?: SxProps<Theme>;
  dividerSx?: SxProps<Theme>;
  onSuccess?: (email?: string) => void;
  onError?: () => void;
};

const GoogleLogin: React.FC<GoogleLoginProps> = ({
  buttonSx,
  dividerSx,
  onSuccess,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [gisInit, setGisInit] = useState(false);
  const oneTapRedirectPath = useRef<string>('');
  const enableOneTap =
    (process.env.NEXT_PUBLIC_ENABLE_ONE_TAP || 'true').toLowerCase() === 'true';

  const handleGoogleCredentialResponse = async (response: any) => {
    let isNewUser = false;
    try {
      setIsLoading(true);
      const credential: string | undefined = response?.credential;
      if (!credential) {
        setIsLoading(false);
        return;
      }

      const data = await userService.socialLogin({token: credential});
      if ((data as any)?.error) {
        throw (data as any).error;
      }
      const appToken = data?.token;
      if (!appToken) throw {message: 'Invalid response from server'};

      const redirectPath = data?.redirectPath as string | undefined;
      isNewUser = redirectPath === '/registration';

      let email: string | undefined;
      try {
        const payload = JSON.parse(atob(credential.split('.')[1]));
        if (payload?.email) {
          email = payload.email;
          localStorage.setItem('google_login_hint', payload.email);
        }
      } catch {}

      try {
        TrackEventUBA('jsGoogleLoginClick', 'click', {
          pageName: getUbaPageName(),
          status: 'googleSigninSuccess',
          sessionUsername:
            sessionStorage.getItem('userId') || data?.seekerId || '',
        });
      } catch (e) {
        console.error('❌ UBA tracking failed:', e);
      }

      onSuccess?.(email);

      if (window && window.location) {
        const redirectPath = data?.redirectPath as string | undefined;
        const resolvedRedirect =
          unwrapRedirect(oneTapRedirectPath.current) ?? '/jobfeed';
        const tb = parseTalentBridge(resolvedRedirect);

        let nextUrl: string;
        console.log('redirectPath', redirectPath);
        console.log('resolvedRedirect', resolvedRedirect);

        if (redirectPath?.startsWith('/registration')) {
          if (tb) {
            const finalDestination = `/talent-bridge-detail/${tb.city}${
              tb.ref ? `?ref=${tb.ref}` : ''
            }`;
            nextUrl = isAppHirist
              ? `/registration/addPersonalDetails?hir=1&source=reg&redirectto=${encodeURIComponent(
                  finalDestination
                )}`
              : '/registration/addPersonalDetails?has=1&source=re';
          } else {
            nextUrl = isAppHirist
              ? `/registration/addPersonalDetails?hir=1&source=reg&redirectto=${encodeURIComponent(
                  resolvedRedirect
                )}`
              : `/registration/addPersonalDetails?has=1&source=re&redirectto=${encodeURIComponent(
                  resolvedRedirect
                )}`;
          }
        } else if (tb) {
          nextUrl = `/talent-bridge-detail/${tb.city}${
            tb.ref ? `?ref=${tb.ref}` : ''
          }`;
        } else {
          nextUrl = resolvedRedirect;
        }

        openUrl(nextUrl, '_self');
      } else {
        openUrl('/jobfeed', '_self');
      }
    } catch (error: any) {
      setIsLoading(false);
      try {
        TrackEventUBA('jsGoogleLoginClick', 'click', {
          pageName: getUbaPageName(),
          status: 'googleSigninFailure',
          sessionUsername: sessionStorage.getItem('userId') || '',
        });
      } catch {}
      onError?.();
    }
  };

  const handleGoogleClick = () => {
    deferTask(() => {
      try {
        const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
        if (!clientId) throw new Error('Google login not configured');

        const redirectUri = `${window.location.origin}/auth/google/callback`;
        const currentPath = window.location.pathname + window.location.search;
        const paramsObj: Record<string, string> = {
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: 'code',
          scope: 'openid email profile',
          state: currentPath,
        };

        const url = `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams(
          paramsObj
        ).toString()}`;

        const pageName = getUbaPageName();

        const onMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          const {type, nextUrl, seekerId} = event.data || {};

        if (type === 'GOOGLE_LOGIN_SUCCESS') {
          window.removeEventListener('message', onMessage);
          try {
            TrackEventUBA('jsGoogleLoginClick', 'click', {
              pageName,
              status: 'googleSigninSuccess',
              sessionUsername:
                sessionStorage.getItem('userId') || seekerId || '',
            });
          } catch {}
          onSuccess?.();
          openUrl(nextUrl, '_self');
        } else if (type === 'GOOGLE_LOGIN_FAILURE') {
          window.removeEventListener('message', onMessage);
          try {
            TrackEventUBA('jsGoogleLoginClick', 'click', {
              pageName,
              status: 'googleSigninFailure',
              sessionUsername: sessionStorage.getItem('userId') || '',
            });
          } catch {}
          onError?.();
          window.location.href = '/login';
        }
      };

        window.addEventListener('message', onMessage);
        window.open(url, 'googleLogin', 'width=500,height=600');
      } catch (e) {
        // no-op
      }
    });
  };

  useEffect(() => {
    let cancelled = false;
    const initOneTap = async () => {
      try {
        if (!enableOneTap) return;
        const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
        if (!clientId) return;
        await ensureGoogleScript();
        // @ts-ignore
        const google = (window as any)?.google;
        if (!google?.accounts?.id) return;
        if (!gisInit) {
          google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: false,
            itp_support: true,
            context: 'signin',
            use_fedcm_for_prompt: true,
          } as any);
          if (!cancelled) setGisInit(true);
        }
        oneTapRedirectPath.current =
          window.location.pathname + window.location.search;
        google.accounts.id.prompt((notification: any) => {
          if (notification?.isNotDisplayed && notification.isNotDisplayed()) {
            // eslint-disable-next-line no-console
            console.warn(
              'Google One Tap not displayed:',
              notification.getNotDisplayedReason &&
                notification.getNotDisplayedReason()
            );
          }
          if (notification?.isSkippedMoment && notification.isSkippedMoment()) {
            // eslint-disable-next-line no-console
            console.warn(
              'Google One Tap skipped:',
              notification.getSkippedReason && notification.getSkippedReason()
            );
          }
        });
      } catch {}
    };
    initOneTap();
    return () => {
      cancelled = true;
    };
    // We intentionally exclude dependencies to prompt once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Divider sx={{mt: '24px', ...dividerSx}}>
        {' '}
        <Typography style={{color: '#000000', opacity: 0.3}}>
          or
        </Typography>{' '}
      </Divider>
      <GoogleButton
        onClick={handleGoogleClick}
        disabled={isLoading}
        sx={{
          px: '20px',
          py: '10px',
          lineHeight: '24px',
          width: '100%',
          height: isAppHirist ? '54px' : '40px',
          fontSize: '16px',
          fontWeight: 600,
          borderRadius: isAppHirist ? '8px' : '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          border: 1.5,
          mt: '24px',
          ...buttonSx,
        }}
      />
      <GlobalLoaderBackdrop open={isLoading} />
    </>
  );
};

export default GoogleLogin;
